import React, { useEffect } from 'react';
import { Route, useLocation, RouteProps, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/';
import { SerializedError } from '@reduxjs/toolkit';
import { getAuthStore, setIsLoggedIn } from 'store/slices';
import { openGenericErrorNotification } from 'helpers/';
import { log } from 'utils/logService';
import {
  useCommitmentLoginFromOutcomeMutation,
  useRefreshMutation,
} from 'store/api';
import { Routes, EventIds } from 'constants/';

export type ProtectedRouteProps = Omit<RouteProps, 'location'>;

let isRefreshing = false;

export const AuthLinkRoute: React.FC<ProtectedRouteProps> = ({
  component: Component,
  path,
  render,
  children,
  ...rest
}) => {
  const location = useLocation();
  const { isLoggedIn } = useAppSelector(getAuthStore);

  const history = useHistory();

  const [loginFromOutcome] = useCommitmentLoginFromOutcomeMutation();

  const [refresh] = useRefreshMutation();

  const dispatch = useAppDispatch();

  const isLoggedInOrAuthComplete = isLoggedIn;

  useEffect(() => {
    if (isLoggedIn) {
      return;
    }

    const getLoggedInFromOutcome = async () => {
      const payload = {
        url: window.location.toString(),
      };

      const loginResult = await loginFromOutcome(payload);
      const loginError = (loginResult as { error: SerializedError }).error;
      if (loginError) {
        log.error(
          'Login from outcome failed',
          EventIds.CommitmentNextActionMutationError,
          loginError
        );
        openGenericErrorNotification();
        throw loginError;
      }

      dispatch(setIsLoggedIn(true));
    };

    if (!isLoggedIn && !isRefreshing) {
      isRefreshing = true;
      refresh().then((result) => {
        if ((result as { error: SerializedError }).error) {
          getLoggedInFromOutcome().catch(() => {
            history.push({
              pathname: Routes.SignIn,
              state: {
                returnUrl: `${location.pathname}${location.search}`,
              },
            });
          });
          return;
        }
        dispatch(setIsLoggedIn(true));
      });
    }
  }, [isLoggedIn, isRefreshing, location.pathname]);

  const renderComponent = (props: any) => {
    if (isLoggedInOrAuthComplete) {
      if (render) {
        return render(props);
      }

      if (Component) {
        return (<Component {...props} />) as any;
      }
    }

    return null;
  };

  if (children) {
    return (
      <Route path={path} {...rest}>
        {isLoggedInOrAuthComplete && children}
      </Route>
    );
  }

  return <Route path={path} render={renderComponent} {...rest} />;
};
